// LaunchWaitOverlay.js - "Your session is starting" screen for zero-warm AppStream launches.
// Used by UserDashboard (Users) and BuyerLaunchPanel (self-assigned Buyers).
//
// Flow: dashboard POSTs its launch endpoint. 200 + streamingUrl -> redirect as before.
//       202 { status: "starting", ... } -> dashboard opens this overlay, which polls /launch-status.
//       When ready: chime + spoken announcement (repeats a few times), tab title flashes, Start button.
//       Start -> POST the launch endpoint again -> fresh streaming URL -> redirect.
// The streaming URL is only requested when the student clicks Start, because AppStream URLs expire quickly.

import React, { useEffect, useRef, useState } from "react";

const POLL_MS = 15000;
const ANNOUNCE_REPEAT_MS = 40000;
const ANNOUNCE_MAX = 4;
const SOUND = {
  chime: "/sounds/session-ready-chime.mp3",
  PART1: "/sounds/session-ready-part1.mp3",
  PART2: "/sounds/session-ready-part2.mp3"
};

// Browsers (especially Safari) block sound that isn't triggered by a click. Call this from the
// Launch button's click handler: it plays the clips silently once so they're allowed to play later.
const audioCache = {};
export function unlockLaunchAudio(application) {
  for (const key of ["chime", application]) {
    if (!SOUND[key]) continue;
    const a = audioCache[key] || new Audio(SOUND[key]);
    audioCache[key] = a;
    a.muted = true;
    a.play().then(() => { a.pause(); a.currentTime = 0; a.muted = false; }).catch(() => { a.muted = false; });
  }
}

function playAnnouncement(application) {
  const chime = audioCache.chime || new Audio(SOUND.chime);
  const voice = audioCache[application] || new Audio(SOUND[application]);
  audioCache.chime = chime; audioCache[application] = voice;
  chime.currentTime = 0;
  chime.onended = () => { setTimeout(() => { voice.currentTime = 0; voice.play().catch(() => {}); }, 300); };
  chime.play().catch(() => { voice.play().catch(() => {}); }); // if the chime is blocked, still try the voice
}

const fmtMinutes = (secs) => {
  const m = Math.max(1, Math.round(secs / 60));
  return m === 1 ? "about 1 minute" : `about ${m} minutes`;
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json"
});

export default function LaunchWaitOverlay({ application, appName, launchEndpoint, initialStatus, onClose }) {
  const [status, setStatus] = useState(initialStatus || {});
  const [ready, setReady] = useState(!!(initialStatus && initialStatus.ready));
  const [remaining, setRemaining] = useState((initialStatus && initialStatus.estimatedSecondsRemaining) || 780);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  mutedRef.current = muted;

  const total = status.estimatedSecondsTotal || 780;

  // Poll the server until a session is available.
  useEffect(() => {
    if (ready) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch("/launch-status", { method: "POST", headers: authHeaders(), body: JSON.stringify({ application }) });
        if (!r.ok) throw new Error(`status ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setError(null);
        setStatus(data);
        setRemaining(data.estimatedSecondsRemaining || 0);
        if (data.ready) setReady(true);
      } catch (e) {
        if (!cancelled) setError("Having trouble checking your session. We'll keep trying...");
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [ready, application]);

  // Smooth countdown between polls.
  useEffect(() => {
    if (ready) return undefined;
    const id = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [ready]);

  // When ready: announce (chime + voice) a few times, and flash the tab title until they click Start.
  useEffect(() => {
    if (!ready) return undefined;
    const originalTitle = document.title;
    const readyTitle = `● Ready: ${appName}`;
    let plays = 0;
    const announce = () => { if (!mutedRef.current && plays < ANNOUNCE_MAX) { plays += 1; playAnnouncement(application); } };
    announce();
    const announceId = setInterval(announce, ANNOUNCE_REPEAT_MS);
    let flip = false;
    const titleId = setInterval(() => { flip = !flip; document.title = flip ? readyTitle : originalTitle; }, 1000);
    return () => { clearInterval(announceId); clearInterval(titleId); document.title = originalTitle; };
  }, [ready, application, appName]);

  // Start: ask for a fresh streaming URL now that the student is here.
  const startSession = async () => {
    setStarting(true);
    setError(null);
    try {
      const r = await fetch(launchEndpoint, { method: "POST", headers: authHeaders(), body: JSON.stringify({ application }) });
      const data = await r.json().catch(() => ({}));
      if (r.status === 200 && data.streamingUrl) {
        window.location.href = data.streamingUrl;
        return;
      }
      if (r.status === 202) {
        // Someone else took the available instance first; keep waiting for the next one.
        setStatus(data);
        setRemaining(data.estimatedSecondsRemaining || 300);
        setReady(false);
        setError("That session was just taken by another player. We're preparing another one for you.");
        return;
      }
      throw new Error(data.reason || data.error || `Request failed (${r.status})`);
    } catch (e) {
      setError(`Could not start ${appName}: ${e.message}`);
    } finally {
      setStarting(false);
    }
  };

  const pct = ready ? 100 : Math.min(95, Math.round(((total - remaining) / total) * 100));

  const box = { background: "#fff", borderRadius: 8, padding: "2rem", maxWidth: 480, width: "90%", textAlign: "center", boxShadow: "0 8px 30px rgba(0,0,0,0.3)" };
  const btn = (bg) => ({ padding: "0.75rem 1.5rem", background: bg, color: "#fff", border: "none", borderRadius: 4, fontSize: "1.1rem", cursor: "pointer" });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={box} role="dialog" aria-live="polite">
        {ready ? (
          <>
            <h2 style={{ marginTop: 0, color: "#2e7d32" }}>{appName} is ready to play</h2>
            <p>Click Start to begin your session.</p>
            <button onClick={startSession} disabled={starting} style={{ ...btn("#4CAF50"), fontSize: "1.3rem", padding: "1rem 2.5rem" }}>
              {starting ? "Starting..." : "Start"}
            </button>
          </>
        ) : (
          <>
            <h2 style={{ marginTop: 0 }}>Your {appName} session is being prepared</h2>
            <p>This usually takes <strong>12 to 15 minutes</strong>. You can leave this page open and do something else.
              You'll hear a chime and an announcement when it's ready.</p>
            <div style={{ background: "#e0e0e0", borderRadius: 4, height: 14, overflow: "hidden", margin: "1rem 0" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: "#2196F3", transition: "width 1s linear" }} />
            </div>
            <p style={{ color: "#555", margin: 0 }}>
              {status.slowerThanUsual ? "Taking a little longer than usual. Almost there..." : `Estimated time remaining: ${fmtMinutes(remaining)}`}
            </p>
          </>
        )}
        {error && <p style={{ color: "#c62828", marginTop: "1rem" }}>{error}</p>}
        <div style={{ marginTop: "1.5rem", display: "flex", justifyContent: "center", gap: "1rem", fontSize: "0.9rem" }}>
          <label style={{ cursor: "pointer" }}>
            <input type="checkbox" checked={muted} onChange={(e) => setMuted(e.target.checked)} /> Mute announcement
          </label>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
